import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
} from '@mui/material';
import DivForm from 'src/components/ModalDialogMUI6/styled/DivForm';
import { STYLE_OPTIONS } from 'src/constants';
import { getActiveSmartLinks, clearActiveSmartLinks } from 'src/actions/smartLinksActions';

const filterOptions = createFilterOptions({
  stringify: ({
    label,
    description,
  }) => `${label} ${description || ''}`,
});

const isOptionEqualToValue = (option, value) => value?.value && value?.value === option?.value;

const renderOptionAutocomplete = (props, option) => (
  <div
    {...props}
    style={{
      alignItems: 'start',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <b>{option.label}</b>
    {option.description && (
      <Box
        sx={{
          color: STYLE_OPTIONS.COLOR_BLUE,
          fontFamily: STYLE_OPTIONS.FONT_FAMILY,
          fontSize: '12px',
        }}
      >
        {option.description}
      </Box>
    )}
  </div>
);

function ModalInsertSmartLink({
  isModalVisible,
  onInsert,
  onCloseModal,
}) {
  const [selectedSmartLink, setSelectedSmartLink] = useState(null);
  const dispatch = useDispatch();

  const { activeSmartLinks } = useSelector((state) => state?.smartLinks || {});
  const { data: smartLinksData, loading } = activeSmartLinks || {};

  const smartLinkOptions = smartLinksData?.map((link) => ({
    value: link?.id,
    label: link?.name,
    code: link?.code,
    description: link?.description,
  })) || [];

  useEffect(() => {
    if (isModalVisible) {
      dispatch(getActiveSmartLinks());
    }
    return () => {
      if (isModalVisible) {
        dispatch(clearActiveSmartLinks());
      }
    };
  }, [isModalVisible, dispatch]);

  const handleClose = () => {
    onCloseModal();
    setSelectedSmartLink(null);
  };

  const handleInsert = () => {
    const smartLinkName = selectedSmartLink?.label || null;
    // Insert token in display format [sl:Name] - will be transformed to [sl:code] on send
    const smartLinkToken = smartLinkName ? `[sl:${smartLinkName}]` : null;
    if (smartLinkToken) {
      onInsert(smartLinkToken);
    }
    handleClose();
  };

  return (
    <Dialog
      open={isModalVisible}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '24px',
        },
      }}
    >
      <Box
        sx={{
          padding: '0 0 0 16px',
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            padding: 0,
          }}
        >
          Select Smart Link & Insert
        </DialogTitle>
      </Box>
      <DivForm>
        <Autocomplete
          options={smartLinkOptions}
          value={selectedSmartLink}
          filterOptions={filterOptions}
          renderOption={renderOptionAutocomplete}
          isOptionEqualToValue={isOptionEqualToValue}
          loading={loading}
          onChange={(e, value) => setSelectedSmartLink(value)}
          renderInput={(params) => (
            <TextField
              label="Smart Link"
              placeholder="Search or select a Smart Link"
              {...params}
            />
          )}
          handleHomeEndKeys
          selectOnFocus
          clearOnBlur
        />
      </DivForm>
      <DialogActions
        sx={{
          padding: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            minWidth: '105px',
            marginRight: '14px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleInsert}
          disabled={!selectedSmartLink?.label}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY,
            minWidth: '105px',
          }}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalInsertSmartLink.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
};

export default ModalInsertSmartLink;
